import React, { useEffect, useRef, useState } from "react";
import { CombinedTimelineDiff } from "./combined-timeline-diff";
import { rasterizeSvg, RasterizedSvg } from "./rasterize";
import { TActivitiesV3, TLinkedMaterial, TLinkedSubmittal } from "../../models";
import { usePrintReadinessSlot } from "../print-readiness";

type SubmittalProps = {
  kind: "submittal";
  submittal: TLinkedSubmittal;
  activityComparison: TActivitiesV3;
  projectId: string;
};

type MaterialProps = {
  kind: "material";
  material: TLinkedMaterial;
  activityComparison: TActivitiesV3;
  projectId: string;
};

type Props = SubmittalProps | MaterialProps;

const IDLE_TIMEOUT_MS = 500;

function scheduleIdle(cb: () => void): () => void {
  const w = window as any;
  if (typeof w.requestIdleCallback === "function") {
    const id = w.requestIdleCallback(cb, { timeout: IDLE_TIMEOUT_MS });
    return () => w.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(cb, 16);
  return () => window.clearTimeout(id);
}

/**
 * Renders the combined timeline offscreen, rasterizes the SVG to a PNG,
 * then swaps in an `<img>`. Unmounting the live SVG tree is the source of
 * the DOM-node savings at 2000+ row scale.
 */
function RasterizedTimelineDiffInner(props: Props) {
  const svgHostRef = useRef<HTMLDivElement>(null);
  const [png, setPng] = useState<RasterizedSvg | null>(null);
  const [error, setError] = useState<string | null>(null);
  // CombinedTimelineDiff renders an error <div> for layout errors instead
  // of an SVG; rasterize can't handle that, so flip back to the live path.
  const [noSvgFallback, setNoSvgFallback] = useState(false);

  const releaseSlot = usePrintReadinessSlot(!png && !noSvgFallback && !error);

  useEffect(() => {
    // Mount-only effect ([] deps): png is always null on the single run, so
    // this early-return for a truthy png can never execute.
    /* v8 ignore next */
    if (png) return undefined;
    // Guards against setState after unmount when rows scroll out of view
    // before rasterize settles; also revokes the orphaned blob URL since
    // the state-keyed cleanup below won't run (png never transitioned).
    let cancelled = false;
    const cancel = scheduleIdle(() => {
      if (cancelled) return;
      const svg = svgHostRef.current?.querySelector("svg");
      if (!svg) {
        // `if (cancelled) return` above already guarantees cancelled is false
        // here, so the !cancelled false-arm is unreachable.
        /* v8 ignore next */
        if (!cancelled) setNoSvgFallback(true);
        return;
      }
      rasterizeSvg(svg as SVGSVGElement)
        .then((result) => {
          if (cancelled) {
            URL.revokeObjectURL(result.url);
            return;
          }
          setPng(result);
        })
        .catch((e) => {
          if (!cancelled) setError(String(e?.message ?? e));
        });
    });
    return () => {
      cancelled = true;
      cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (png || noSvgFallback || error) releaseSlot();
  }, [png, noSvgFallback, error, releaseSlot]);

  // Revoke blob URL on unmount/replace — the browser's blob store grows
  // unbounded otherwise.
  useEffect(() => {
    const url = png?.url;
    if (!url) return undefined;
    return () => URL.revokeObjectURL(url);
  }, [png?.url]);

  if (error || noSvgFallback) {
    return <CombinedTimelineDiff {...(props as any)} />;
  }

  if (png) {
    return (
      <img
        src={png.url}
        width={png.width}
        height={png.height}
        alt="Before/After schedule comparison"
        loading="eager"
        decoding="async"
        className="block max-w-full h-auto"
      />
    );
  }

  // Zero-size clipped offscreen host — `left: -99999px` would be picked
  // up by parent `overflow: auto` and grow the scrollable region.
  return (
    <div
      ref={svgHostRef}
      aria-hidden
      style={{
        position: "absolute",
        width: 0,
        height: 0,
        overflow: "hidden",
        clip: "rect(0 0 0 0)",
        pointerEvents: "none"
      }}
    >
      <CombinedTimelineDiff {...(props as any)} />
    </div>
  );
}

export const RasterizedTimelineDiff = React.memo(RasterizedTimelineDiffInner);
